import classNames from 'classnames'
import { InfoElement } from 'components'
import { selectOmsOrigins } from 'data/selectors'
import React, { Fragment } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { currencyToShort, formatNumberWithCurrency, getDifferenceWithPrecision, thousandsSeparator } from 'utils'

import Box from '@mui/material/Box'
import Divider from '@mui/material/Divider'
import Grid from '@mui/material/Grid'

import useStyles from './useStyles'

export interface OrdersOriginsDetailsProps {
  dataKey: string
  type: 'revenue' | 'ordersValue'
}

function OrdersOriginsDetails({ dataKey, type }: OrdersOriginsDetailsProps) {
  const { t: translate } = useTranslation()
  const classes = useStyles()
  const { data: rows } = useSelector(selectOmsOrigins({ dataKey }))
  const descriptionKey = type === 'ordersValue' ? 'ordersByOriginAndSource' : 'revenueByOriginAndSource'
  const description = translate(`revenue.details.${descriptionKey}`)
  const summary = rows.reduce(
    ({ ecom, necom }, row) => ({
      ecom: ecom + (row[`${type}EComCurrent`] || 0),
      necom: necom + (row[`${type}NEComCurrent`] || 0),
    }),
    { ecom: 0, necom: 0 },
  )
  const currency = rows?.[0]?.currency
  const getPercentDifference = (number?: number | null) =>
    getDifferenceWithPrecision({
      handleNullCase: true,
      number,
      precision: 0,
      translate,
    })

  if (!rows.length) return null

  return (
    <div className={classes.wrapper}>
      {description && <div className={classes.description}>{description}</div>}

      <Grid container>
        <Grid container>
          <Grid item xs={3} className={classNames(classes.origin, classes.header)}>
            {translate('revenue.details.origin')}
          </Grid>
          <Grid item xs={3} className={classes.header}>
            {translate('revenue.details.warehouse')}
          </Grid>
          <Grid item xs={3} className={classes.header}>
            {translate('revenue.details.stores')}
          </Grid>
          <Grid item xs={3} className={classes.header}>
            {translate('returnsValueDetails.sum')}
          </Grid>
        </Grid>

        {rows.map(row => {
          const sum = Math.round((row[`${type}EComCurrent`] || 0) + (row[`${type}NEComCurrent`] || 0))

          return (
            <Fragment key={row.origin}>
              <Box width="100%">
                <Divider />
              </Box>

              <Grid container>
                <Grid item xs={3} className={classNames(classes.origin, classes.value)}>
                  {row.origin}
                </Grid>

                <Grid item xs={3} className={classes.value}>
                  <Grid container item xs={8} justifyContent="flex-end">
                    {formatNumberWithCurrency({
                      currency: row.currency,
                      number: row[`${type}EComCurrent`],
                      translate,
                    })}
                  </Grid>
                  <Grid container item xs={4} ml="0.5rem">
                    <InfoElement
                      className={classes.percent}
                      percentDifference={getPercentDifference(row[`${type}EComShareCurrent`])}
                    />
                  </Grid>
                </Grid>

                <Grid item xs={3} className={classes.value}>
                  <Grid container item xs={8} justifyContent="flex-end">
                    {formatNumberWithCurrency({
                      currency: row.currency,
                      number: row[`${type}NEComCurrent`],
                      translate,
                    })}
                  </Grid>
                  <Grid container item xs={4} ml="0.5rem">
                    <InfoElement
                      className={classes.percent}
                      percentDifference={getPercentDifference(row[`${type}NEComShareCurrent`])}
                    />
                  </Grid>
                </Grid>

                <Grid item xs={3} className={classNames(classes.value, classes.sum)}>
                  <Grid container item xs={10} justifyContent="flex-end">
                    {thousandsSeparator(sum)} {currencyToShort(row.currency)}
                  </Grid>
                </Grid>
              </Grid>
            </Fragment>
          )
        })}

        <Box width="100%">
          <Divider />
        </Box>

        <Grid container className={classes.footer}>
          <Grid item xs={3} className={classNames(classes.origin, classes.footerCell)}>
            <Grid container item xs={7}>
              {translate('revenue.details.summary')}
            </Grid>
          </Grid>

          <Grid item xs={3} className={classes.footerCell} justifyContent="flex-end">
            <Grid container item xs={8} justifyContent="flex-end">
              {formatNumberWithCurrency({
                currency: currency,
                number: summary.ecom,
                translate,
              })}
            </Grid>
            <Grid container item xs={4} ml="0.5rem">
              <InfoElement className={classes.percent} percentDifference={getPercentDifference(summary.ecom ? 1 : null)} />
            </Grid>
          </Grid>

          <Grid item xs={3} className={classes.footerCell} justifyContent="flex-end">
            <Grid container item xs={8} justifyContent="flex-end">
              {formatNumberWithCurrency({
                currency: currency,
                number: summary.necom,
                translate,
              })}
            </Grid>
            <Grid container item xs={4} ml="0.5rem">
              <InfoElement className={classes.percent} percentDifference={getPercentDifference(summary.necom ? 1 : null)} />
            </Grid>
          </Grid>

          <Grid item xs={3} className={classNames(classes.footerCell, classes.sum)}>
            <Grid container item xs={10} justifyContent="flex-end">
              {thousandsSeparator(Math.round(summary.ecom + summary.necom))} {currencyToShort(currency)}
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </div>
  )
}

export default OrdersOriginsDetails
